import React, { useRef, useState, useEffect, useCallback } from 'react';

import './css/ListingContent.css';

const CalculateInstallment = (price, manufactured_year) => {
  const currentYear = new Date().getFullYear();
  const tenure = Math.min(15 - (currentYear - manufactured_year), 9);
  if(tenure < 5) {
    return (" ");
  } else {
    const installment = 0.075 * price * (1 + 0.035 * tenure) / tenure;
    return (`RM ${Math.ceil(installment).toLocaleString('en-US')} / month (${tenure} years)`)
  }
}

const Spinner = () => {
  return (
    <div className="spinner-container">
      <svg className="spinner" viewBox="0 0 50 50">
        <circle className="spinner-path" cx="25" cy="25" r="20" fill="none" strokeWidth="4"></circle>
      </svg>
    </div>
  );
};

const CatalogCard = (props) => {
  const [titleOverflow, setTitleOverflow] = useState(false);
  const titleRef = useRef(null);
  const { detail, goToReport } = props;
  const { uuid, manufacturer, model, manufactured_year, price, mileage, transmission, cc, fuel_type } = detail;
  useEffect(() => {
    const checkOverflow = () => {
      if (titleRef.current) {
        // For horizontal overflow
        setTitleOverflow(titleRef.current.scrollWidth > titleRef.current.offsetWidth); 
      }
    };
    checkOverflow(); // Initial check
    window.addEventListener('resize', checkOverflow);
    return () => {
      window.removeEventListener('resize', checkOverflow);
    };
  }, []);
  const cardTitle = `${manufactured_year} ${manufacturer} ${model} ${(cc/1000).toFixed(1)}`;
  const titleString = titleOverflow ? `${cardTitle}    ${cardTitle}` : cardTitle;
  return (
    <div className="card" onClick={() => goToReport(`/report/${uuid}`)}>
      <img src={ `/images/${uuid}/advertisement/advertisement_000.jpg` } />
      <div className="card-body">
        <div className="price">RM{price.toLocaleString('en-US')}</div>
        <div className="installment">{CalculateInstallment(price, manufactured_year)}</div>
        <div className="title">{cardTitle}</div>
        <div className="quick-info">
          <div className="info-item">
            <span className="label">Mileage</span>
            <span className="value">🚗 {mileage.toLocaleString('en-US')} KM</span>
          </div>
          <div className="info-item">
            <span className="label">Transmission</span>
            <span className="value">⚙️ {transmission}</span>
          </div>
          <div className="info-item">
            <span className="label">Fuel</span>
            <span className="value">⛽ {fuel_type}</span>
          </div>
          <div className="info-item">
            <span className="label">Location</span>
            <span className="value">📍 Johor Bahru</span>
          </div>
        </div>
        <div onClick={() => goToReport(`/report/${uuid}`)} className="btn" style={{cursor:"pointer"}}>
          View Inspection Report
        </div>
      </div>
    </div>
  );
}

const CatalogCardMobile = (props) => {
  const [titleOverflow, setTitleOverflow] = useState(false);
  const titleRef = useRef(null);
  const { detail, goToReport } = props;
  const { uuid, manufacturer, model, manufactured_year, price, mileage, transmission, cc, fuel_type } = detail;
  useEffect(() => {
    const checkOverflow = () => {
      if (titleRef.current) {
        // For horizontal overflow
        setTitleOverflow(titleRef.current.scrollWidth > titleRef.current.offsetWidth); 
      }
    };
    checkOverflow(); // Initial check
    window.addEventListener('resize', checkOverflow);
    return () => {
      window.removeEventListener('resize', checkOverflow);
    };
  }, []);
  const cardTitle = `${manufactured_year} ${manufacturer} ${model} ${(cc/1000).toFixed(1)}`;
  const titleString = titleOverflow ? `${cardTitle}    ${cardTitle}` : cardTitle;
  return (
    <div className="card-mobile" onClick={() => goToReport(`/report/${uuid}`)}>
      <div className="image-container-mobile">
        <img src={ `/images/${uuid}/advertisement/advertisement_000.jpg` } />
        <div className="mobile-price-div">
          <h3 className="mobile-price-text">RM{price.toLocaleString('en-US')}</h3>
          <p className="mobile-installment-text">{CalculateInstallment(price, manufactured_year)}</p>
        </div>
      </div>
      <div className="card-body">
        <div className="title">{cardTitle}</div>	
        <div className="info-item-mobile">
          <span className="label">Mileage</span>
          <span className="value">{mileage.toLocaleString('en-US')} KM</span>
        </div>
        <div className="info-item-mobile">
          <span className="label">Transmission</span>
          <span className="value">{transmission}</span>
        </div>
        <div className="info-item-mobile">
          <span className="label">Fuel</span>
          <span className="value">{fuel_type}</span>
        </div>
        <div className="info-item-mobile">
          <span className="label">Location</span>
          <span className="value">📍 Johor Bahru</span>
        </div>
      </div>
    </div>
  );
}

const ListingContent = ({hist, price, mile, searchTerm, updateSearchTerm, goToReport}) => {
  const [data, setData] = useState([]);
  const [bottomReached, setBottomReached] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [inputValue, setInputValue] = useState('');

  const observerRef = useRef(null);
  const loaderRef = useCallback((node) => {
    // 1. Clean up the previous observer if it exists
    if (observerRef.current) {
      observerRef.current.disconnect();
    }

    // 2. If the node doesn't exist (unmounted), stop here
    if (!node) return;

    // 3. Instantiate the observer on the new DOM node
    observerRef.current = new IntersectionObserver((entries) => {
      const target = entries[0];
    
      // CRITICAL: Since this callback recreates when states change, 
      // it always reads the fresh values of isLoading and bottomReached
      if (target.isIntersecting && !isLoading && !bottomReached) {
        fetchData(data.length);
      }
    }, { rootMargin: '100px' });

    // 4. Start observing the new node
    observerRef.current.observe(node);
  }, [isLoading, bottomReached, data.length]);

  const fetchData = async (startIndex) => {    
    if (isLoading) return;
    setIsLoading(true);
    var histString = "";
    if(hist !== 0) {
      histString = `hist=${hist}&`;
    }
    var priceString = "";
    if(price === 1) {
        priceString = "min_price=0&max_price=50000&";
    } else if(price === 2) {
        priceString = "min_price=50000&max_price=100000&";
    } else if(price === 3) {
        priceString = "min_price=100000&max_price=150000&";
    } else if(price === 4) {
        priceString = "min_price=150000&";
    }
    var mileString = "";
    if(mile === 1) {
        mileString = "min_mile=0&max_mile=50000&";
    } else if(mile === 2) {
        mileString = "min_mile=50000&max_mile=100000&";
    } else if(mile === 3) {
        mileString = "min_mile=100000&";
    }
    try {
      const response = await fetch(`/catalog/${startIndex}?${histString}${priceString}${mileString}${searchTerm}`);
      const newData = await response.json();
      if(newData.length < 20) {
        setBottomReached(true);
      }
      setData([...data, ...newData]);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const updateFilter = async (startIndex) => {    
    if (isLoading) return;
    setIsLoading(true);
    var histString = "";
    if(hist !== 0) {
      histString = `hist=${hist}&`;
    }
    var priceString = "";
    if(price === 1) {
      priceString = "min_price=0&max_price=50000&";
    } else if(price === 2) {
      priceString = "min_price=50000&max_price=100000&";
    } else if(price === 3) {
      priceString = "min_price=100000&max_price=150000&";
    } else if(price === 4) {
      priceString = "min_price=150000&";
    }
    var mileString = "";
    if(mile === 1) {
      mileString = "min_mile=0&max_mile=50000&";
    } else if(mile === 2) {
      mileString = "min_mile=50000&max_mile=100000&";
    } else if(mile === 3) {
      mileString = "min_mile=100000&";
    }
    try {
      const response = await fetch(`/catalog/${startIndex}?${histString}${priceString}${mileString}${searchTerm}`);
      const newData = await response.json();
      if(newData.length < 20) {
        setBottomReached(true);
      }
      setData(newData);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setIsLoading(false);
    }
  };
  useEffect(() => {
    updateFilter(0);
  }, [hist, price, mile, searchTerm]);
  
  {/*useEffect(() => {
    const currentLoader = loaderRef.current;
    if (!currentLoader) return;

    const observer = new IntersectionObserver((entries) => {
      const target = entries[0];
      // Only execute fetch operations if the component is not currently querying a process
      if (target.isIntersecting && !isLoading && !bottomReached) {
        fetchData(data.length);
      }
    }, { rootMargin: '100px' });

    observer.observe(currentLoader);

    // CRITICAL: Cleanup old observers to prevent memory leaks when component unmounts
    return () => {
      if (currentLoader) {
        observer.unobserve(currentLoader);
      }
    };
  }, [isLoading, data, hist, price, mile, searchTerm]);*/}

  const handleInputChange = (event) => {
    setInputValue(event.target.value);
  };

  const handleButtonClick = () => {
    setData([]);
    setBottomReached(false);
    if(inputValue != '') {
      updateSearchTerm(`term=${inputValue}`);
    } else {
      updateSearchTerm('');
    }
  };

  const clearSearch = () => {
    setData([]);
    setBottomReached(false);
    setInputValue('');
    updateSearchTerm('');
  };

  const numEntries = (data.length >= 20) ? "20+ " : `${data.length}`;

  return (
    <div className="content">
      <div className="topbar">
        <input
          className="search"
          type="text"
          value={inputValue} 
          onChange={handleInputChange}
          placeholder="Search vehicle make or model"
        />
        <div className="search-btn">
          <button className="btn" onClick={handleButtonClick}>
            <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" fill="currentColor" className="search-icon" viewBox="0 0 16 16">
              <path d="M11.742 10.344a6.5 6.5 0 1 0-1.397 1.398h-.001q.044.06.098.115l3.85 3.85a1 1 0 0 0 1.415-1.414l-3.85-3.85a1 1 0 0 0-.115-.1zM12 6.5a5.5 5.5 0 1 1-11 0 5.5 5.5 0 0 1 11 0"/>
            </svg>
          </button>
          <button className="btn" onClick={clearSearch}>
            Clear
          </button>
        </div>
        <div style={{flexGrow: 1}}></div>
        <h2>{numEntries} Verified Vehicles</h2>
      </div>
      <div className="grid">
        {data.map((item) => (
          <div key={item.uuid}>
            <CatalogCard detail={item} goToReport={goToReport}/>
            <CatalogCardMobile detail={item} goToReport={goToReport}/>
          </div>
        ))}
        <div ref={loaderRef} style={{ padding: '20px', textAlign: 'center', fontWeight: 'bold' }}>
          <Spinner />
        </div>
      </div>
    </div>
  );
};

export default ListingContent;
